import SEO from "@/components/SEO";
import Breadcrumb from "@/components/Breadcrumb";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import InstantQuoteCTA from "@/components/InstantQuoteCTA";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Phone, CheckCircle2, Heart, Users, Camera, Clock, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import ServiceFAQ from "@/components/ServiceFAQ";
import { generateLocalBusinessSchema } from "@/lib/schemaGenerator";
import weddingCoupleImg from "@/assets/wedding-couple.jpg";
const WeddingTransportation = () => {
  const faqs = [{
    question: "How far in advance should I book wedding transportation?",
    answer: "We recommend booking as soon as you have your wedding date and venue confirmed, especially for peak wedding season (April-June and September-November). Popular dates book 6-12 months in advance. However, we can often accommodate last-minute requests based on availability."
  }, {
    question: "Do you charge by the hour or flat rate?",
    answer: "Wedding transportation is typically quoted as a package based on hours of service, number of vehicles, and locations. We provide transparent, all-inclusive pricing with no hidden fees. Gratuity is at your discretion."
  }, {
    question: "What happens if my timeline changes on the wedding day?",
    answer: "We build flexibility into wedding transportation. Our chauffeurs stay in communication with you or your wedding coordinator and adapt to timing changes. We understand that wedding days rarely run exactly on schedule."
  }, {
    question: "Can you coordinate with my wedding planner?",
    answer: "Absolutely! We love working with wedding planners and coordinators. We'll integrate into your timeline, communicate directly with your planner, and ensure transportation aligns perfectly with your overall schedule."
  }, {
    question: "Can you shuttle guests?",
    answer: "We can schedule multiple SUV trips based on your guest count (up to 6 passengers per trip)."
  }, {
    question: "Do you serve destinations outside San Antonio?",
    answer: "Yes! We regularly serve weddings throughout the Texas Hill Country including Boerne, New Braunfels, Fredericksburg, Dripping Springs, and beyond. Distance is factored into our pricing."
  }];
  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://ricksblackcarservicesantonio.com/services/wedding-transportation#service",
    "name": "Wedding Transportation San Antonio",
    "serviceType": "Wedding Transportation",
    "provider": {
      "@type": "LocalBusiness",
      "@id": "https://ricksblackcarservicesantonio.com/#business",
      "name": "Rick's Black Car Service",
      "telephone": "+1-210-296-7409",
      "url": "https://ricksblackcarservicesantonio.com"
    },
    "areaServed": [
      {
        "@type": "City",
        "name": "San Antonio",
        "addressRegion": "Texas"
      },
      {
        "@type": "City",
        "name": "Boerne",
        "addressRegion": "Texas"
      },
      {
        "@type": "City",
        "name": "New Braunfels",
        "addressRegion": "Texas"
      },
      {
        "@type": "Region",
        "name": "Texas Hill Country"
      }
    ],
    "description": "Elegant wedding transportation in San Antonio and Hill Country. Professional chauffeur service for bride, groom, bridal party, and guests with coordinated timelines and photo-ready luxury vehicles. Over 5 years serving San Antonio weddings.",
    "image": [
      "https://ricksblackcarservicesantonio.com/og-image.jpg"
    ],
    "category": "Wedding Transportation Service",
    "serviceOutput": "Luxury wedding day transportation with coordinated arrival and departure schedules",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Wedding Transportation Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "name": "Bride & Groom Transportation",
          "description": "Luxury Audi Q7 for bride and groom with photo-ready presentation, red carpet service, and timeline coordination",
          "price": "540",
          "priceCurrency": "USD",
          "priceValidUntil": "2026-12-31",
          "availability": "https://schema.org/InStock",
          "itemOffered": {
            "@type": "Service",
            "name": "Bride & Groom Transportation (4 hours)"
          }
        },
        {
          "@type": "Offer",
          "name": "Bridal Party Transportation",
          "description": "Multiple trips for bridal party and family with flexible scheduling",
          "price": "810",
          "priceCurrency": "USD",
          "priceValidUntil": "2026-12-31",
          "availability": "https://schema.org/InStock",
          "itemOffered": {
            "@type": "Service",
            "name": "Bridal Party Transportation (6 hours)"
          }
        },
        {
          "@type": "Offer",
          "name": "Full Wedding Day Package",
          "description": "Complete wedding day coverage including ceremony, photos, and reception transportation",
          "price": "1080",
          "priceCurrency": "USD",
          "priceValidUntil": "2026-12-31",
          "availability": "https://schema.org/InStock",
          "itemOffered": {
            "@type": "Service",
            "name": "Full Wedding Day Package (8 hours)"
          }
        }
      ]
    },
    "offers": {
      "@type": "AggregateOffer",
      "url": "https://ricksblackcarservicesantonio.com/services/wedding-transportation",
      "priceCurrency": "USD",
      "lowPrice": "540",
      "highPrice": "1080",
      "offerCount": "3",
      "priceValidUntil": "2026-12-31",
      "availability": "https://schema.org/InStock",
      "validFrom": "2024-01-01",
      "seller": {
        "@type": "LocalBusiness",
        "name": "Rick's Black Car Service",
        "telephone": "+1-210-296-7409"
      }
    },
    "priceRange": "$135/hour (4-hour minimum)",
    "additionalType": "https://schema.org/TaxiService",
    "audience": {
      "@type": "Audience",
      "audienceType": "Brides, Grooms, Wedding Planners"
    },
    "availableChannel": {
      "@type": "ServiceChannel",
      "servicePhone": {
        "@type": "ContactPoint",
        "telephone": "+1-210-296-7409",
        "contactType": "Reservations"
      },
      "serviceUrl": "https://ricksblackcarservicesantonio.com/services/wedding-transportation"
    }
  };
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
  return <>
      <SEO title="Wedding Transportation San Antonio | Rick's Black Car Service" description="Elegant bridal transportation in our 2025 Audi Q7 Black Optic SUV. Seats up to 6 guests. Serving San Antonio & Hill Country venues. Flexible packages for your special day." keywords="wedding transportation san antonio, bridal car service, wedding limo san antonio, luxury wedding transportation, hill country wedding transportation, bridal party transportation" canonicalPath="/services/wedding-transportation" schema={[schemaMarkup, faqSchema]} />

      <div className="min-h-screen">
        <Navbar />
        <main className="pt-20">
          <Breadcrumb 
            items={[
              { name: "Services", url: "/services" },
              { name: "Wedding Transportation", url: "/services/wedding-transportation" }
            ]}
          />

          {/* Hero Section */}
          <section className="relative h-[60vh] overflow-hidden flex items-center justify-center">
            <div className="absolute inset-0 z-0">
              <img src={weddingCoupleImg} alt="Wedding transportation San Antonio - luxury bridal car service" className="w-full h-full object-cover brightness-75" />
              <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/50 to-background/70" />
            </div>
            <div className="container mx-auto px-4 relative z-10 text-center">
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-bold text-foreground mb-6">
                San Antonio Wedding Transportation: Luxury Bridal Car Service
              </h1>
              <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto">
                Elegant Luxury Transportation for Your Special Day
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button className="btn-luxury text-lg px-8 py-6" asChild>
                  <a href="tel:+12102967409">
                    <Phone className="w-5 h-5 mr-2" />
                    Call Now: 210-296-7409
                  </a>
                </Button>
                <Button className="btn-luxury text-lg px-8 py-6" asChild>
                  <a href="https://ricksblackcarservicesantonio.square.site/" target="_blank" rel="noopener noreferrer">
                    Book Your Private Executive Car Service Today
                  </a>
                </Button>
              </div>
            </div>
          </section>

          {/* Main Content */}
          <section className="py-20 luxury-gradient">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <div className="prose prose-lg max-w-none">
                  <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                    Make your special day unforgettable with elegant <strong className="text-foreground">Wedding Transportation San Antonio</strong> from Rick's Black Car Service. As your premier <strong className="text-foreground">Luxury Bridal Car Service</strong>, we specialize in wedding chauffeur services throughout San Antonio and the beautiful Texas Hill Country. From the bride and groom's grand arrival to coordinating transportation for your entire bridal party and guests, we ensure every detail is perfect.
                  </p>
                  
                  <p className="text-muted-foreground text-lg leading-relaxed mb-8">
                    Our professional chauffeurs and photo-ready luxury vehicles add sophistication and style to your wedding day while providing stress-free, reliable <strong className="text-foreground">bridal party transportation</strong>. With over 5 years of experience serving San Antonio weddings, we understand the importance of punctuality, professionalism, and creating magical moments.
                  </p>

                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-6 mt-12">
                    Complete Wedding Car Service for Your Special Day
                  </h2>
                  
                  <h3 className="text-2xl font-serif font-bold text-foreground mb-4 mt-8">
                    Bride & Groom Luxury Transportation
                  </h3>
                  <p className="text-muted-foreground text-lg leading-relaxed mb-4">
                    Your wedding day deserves the finest transportation. Our premium black car service provides elegant Luxury SUV for the bride and groom, creating a memorable entrance and exit at your ceremony and reception.
                  </p>
                  <ul className="space-y-2 text-muted-foreground text-lg mb-8">
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                      <span>Photo-ready, immaculate SUV, seats up to 6</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                      <span>Pre-ceremony trips for bridal party/family</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                      <span>Couple's private SUV between venues/photo stops</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                      <span>Guest trips to reception (multiple SUV runs as needed)</span>
                    </li>
                  </ul>

                  <h3 className="text-2xl font-serif font-bold text-foreground mb-4 mt-8">
                    Bridal Party Transportation
                  </h3>
                  <p className="text-muted-foreground text-lg leading-relaxed mb-4">
                    Keep your bridal party together and on schedule with our dedicated bridal party shuttle service. We coordinate transportation for bridesmaids, groomsmen, and the entire wedding party throughout your special day.
                  </p>
                  <ul className="space-y-2 text-muted-foreground text-lg mb-8">
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                      <span>Bridesmaids shuttle from getting-ready location to venue</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                      <span>Groomsmen group transportation</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                      <span>Coordinated arrivals for perfect timing</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                      <span>Multiple vehicle options for larger parties</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                      <span>Professional chauffeurs who understand wedding schedules</span>
                    </li>
                  </ul>

                  <h3 className="text-2xl font-serif font-bold text-foreground mb-4 mt-8">
                    Wedding Guest Transportation
                  </h3>
                  <p className="text-muted-foreground text-lg leading-relaxed mb-4">
                    Ensure your guests arrive safely and on time with our professional guest shuttle service. Perfect for transporting guests between hotels and your wedding venue, especially important for Hill Country or destination weddings.
                  </p>
                  <ul className="space-y-2 text-muted-foreground text-lg mb-8">
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                      <span>Hotel to ceremony venue shuttles</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                      <span>Ceremony to reception transportation</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                      <span>Evening shuttle service back to hotels</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                      <span>Multiple pickup locations coordinated</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                      <span>Continuous shuttle loops for large guest lists</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                      <span>Safe transportation so guests can enjoy the celebration</span>
                    </li>
                  </ul>

                  <h3 className="text-2xl font-serif font-bold text-foreground mb-4 mt-8">
                    Parents & VIP Family Transportation
                  </h3>
                  <p className="text-muted-foreground text-lg leading-relaxed mb-8">
                    Honor your parents and special family members with dedicated luxury transportation. Our chauffeurs provide respectful, professional service for the most important people in your life.
                  </p>
                </div>

                {/* CTA Card */}
                <Card className="card-luxury my-12 border-2 border-primary/20">
                  <CardContent className="p-10 text-center">
                    <h2 className="text-3xl font-serif font-bold text-foreground mb-4">
                      Book Your Wedding Transportation
                    </h2>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
                      <Button className="btn-luxury text-lg px-8 py-6 w-full sm:w-auto" asChild>
                        <a href="tel:+12102967409">
                          📞 Call Now: 210-296-7409
                        </a>
                      </Button>
                      <Button className="btn-luxury text-lg px-8 py-6 w-full sm:w-auto" asChild>
                        
                      </Button>
                    </div>
                    <p className="text-muted-foreground text-lg">
                      Book Early for Your Wedding Date | Professional Service | Elegant Fleet
                    </p>
                  </CardContent>
                </Card>

                {/* Popular Wedding Venues */}
                <Card className="card-luxury my-12">
                  <CardContent className="p-10">
                    <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-6">
                      San Antonio & Hill Country Wedding Venues We Serve
                    </h2>
                    <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                      Rick's Black Car Service provides wedding transportation to all major wedding venues throughout San Antonio, Boerne, New Braunfels, Fredericksburg, and the entire Texas Hill Country region.
                    </p>

                    <h3 className="text-2xl font-serif font-bold text-foreground mb-4 mt-6">
                      Popular San Antonio Wedding Venues:
                    </h3>
                    

                    <h3 className="text-2xl font-serif font-bold text-foreground mb-4 mt-6">
                      Hill Country Wedding Venues:
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-primary" />
                        <span className="text-muted-foreground"><strong className="text-foreground">Camp Lucy</strong> - Dripping Springs luxury</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-primary" />
                        <span className="text-muted-foreground"><strong className="text-foreground">Gruene Estate</strong> - Historic venue</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-primary" />
                        <span className="text-muted-foreground"><strong className="text-foreground">Chandelier Grove</strong> - Boerne ranch</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-primary" />
                        <span className="text-muted-foreground"><strong className="text-foreground">Vista West Ranch</strong> - Hill Country views</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-primary" />
                        <span className="text-muted-foreground"><strong className="text-foreground">Bella Springs</strong> - Boerne event venue</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-primary" />
                        <span className="text-muted-foreground"><strong className="text-foreground">Tapatio Springs</strong> - Resort weddings</span>
                      </div>
                    </div>
                    <p className="text-muted-foreground text-lg leading-relaxed mt-6">
                      We're familiar with all major venues in the area and work closely with venue coordinators, wedding planners, and photographers to ensure perfect timing and seamless coordination throughout your day.
                    </p>
                  </CardContent>
                </Card>

                {/* Wedding Planning Information */}
                <div className="prose prose-lg max-w-none my-12">
                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-6">
                    Wedding Transportation Planning
                  </h2>
                  
                  <h3 className="text-2xl font-serif font-bold text-foreground mb-4">
                    Timeline Coordination
                  </h3>
                  <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                    Our experienced wedding transportation specialists work with you and your wedding planner to create a detailed transportation timeline. We understand wedding day schedules and build in appropriate buffer time to ensure everything runs smoothly.
                  </p>

                  <h3 className="text-2xl font-serif font-bold text-foreground mb-4">
                    What's Included in Our Wedding Service:
                  </h3>
                  <ul className="space-y-3 text-muted-foreground text-lg mb-8">
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span>Personalized consultation and transportation planning</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span>Professional, uniformed chauffeurs experienced in weddings</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span>Immaculate luxury vehicles detailed before your event</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span>Vehicle decorations (ribbons, bows, "Just Married" signs available)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span>Red carpet service for bride's arrival (available upon request)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span>Complimentary champagne for bride and groom (where legal)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span>Assistance with dress, train, and veil</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span>Umbrellas provided for inclement weather</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span>Coordination with photographers for perfect vehicle shots</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span>Backup vehicles and drivers on standby</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span>Multiple pickup and drop-off locations</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span>Flexible timing to accommodate photography sessions</span>
                    </li>
                  </ul>

                  <h3 className="text-2xl font-serif font-bold text-foreground mb-4 mt-8">
                    Vehicle Options for Your Wedding
                  </h3>
                  <p className="text-muted-foreground text-lg leading-relaxed mb-4">
                    Our luxury SUV accommodates up to 6 passengers with ample space for gowns and formal wear.
                  </p>
                  <ul className="space-y-3 text-muted-foreground text-lg mb-8">
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span><strong className="text-foreground">Luxury SUV:</strong> Photo-ready, immaculate, seats up to 6</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span><strong className="text-foreground">Multiple Trips:</strong> We can coordinate several SUV trips for larger wedding parties</span>
                    </li>
                  </ul>
                </div>

                {/* Wedding Packages */}
                <Card className="card-luxury my-12">
                  <CardContent className="p-10">
                    <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-8">
                      Wedding Transportation Packages
                    </h2>
                    
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-2xl font-serif font-bold text-foreground mb-3">The Classic Package</h3>
                        <p className="text-muted-foreground text-lg leading-relaxed">
                          Ideal for intimate weddings. Includes luxury SUV for bride and groom with up to 6 hours of service covering ceremony, photos, and reception.
                        </p>
                      </div>

                      <div>
                        <h3 className="text-2xl font-serif font-bold text-foreground mb-3">The Deluxe Package</h3>
                        <p className="text-muted-foreground text-lg leading-relaxed">
                          Our most popular option. Includes transportation for bride, groom, and bridal party with coordinated SUV trips, full-day service, and timeline coordination.
                        </p>
                      </div>

                      <div>
                        <h3 className="text-2xl font-serif font-bold text-foreground mb-3">The Grand Package</h3>
                        <p className="text-muted-foreground text-lg leading-relaxed">
                          Complete wedding transportation solution including bride, groom, bridal party, and guest shuttle service with multiple coordinated SUV trips. Full coordination from start to finish.
                        </p>
                      </div>

                      <div>
                        <h3 className="text-2xl font-serif font-bold text-foreground mb-3">Custom Packages Available</h3>
                        <p className="text-muted-foreground text-lg leading-relaxed">
                          Every wedding is unique. We create custom transportation packages tailored to your specific needs, venue locations, guest count, and schedule. Contact us for a personalized quote.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Why Choose Us */}
                <div className="prose prose-lg max-w-none my-12">
                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-6">
                    Why Choose Rick's Black Car Service for Your Wedding?
                  </h2>
                  <ul className="space-y-3 text-muted-foreground text-lg">
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span><strong className="text-foreground">Wedding Specialists:</strong> Over 10 years serving San Antonio weddings</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span><strong className="text-foreground">Perfect Track Record:</strong> Never missed a wedding pickup - 100% reliability</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span><strong className="text-foreground">Attention to Detail:</strong> We understand every moment matters on your special day</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span><strong className="text-foreground">Professional Chauffeurs:</strong> Courteous, well-groomed, experienced drivers</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span><strong className="text-foreground">Immaculate Vehicles:</strong> Detailed and inspected before every wedding</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span><strong className="text-foreground">Flexible Scheduling:</strong> We adapt to your timeline, not the other way around</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span><strong className="text-foreground">Venue Knowledge:</strong> Familiar with all major San Antonio and Hill Country venues</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span><strong className="text-foreground">Planner Friendly:</strong> We work seamlessly with wedding coordinators</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span><strong className="text-foreground">Backup Plans:</strong> Always prepared with contingency vehicles and drivers</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                      <span><strong className="text-foreground">Stress-Free Experience:</strong> You focus on getting married, we handle transportation</span>
                    </li>
                  </ul>
                </div>

                {/* Testimonials */}
                <Card className="card-luxury my-12">
                  <CardContent className="p-10">
                    <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-8 text-center">
                      What Wedding Couples Say
                    </h2>
                    <div className="space-y-6">
                      <div className="border-l-4 border-primary pl-6">
                        <p className="text-muted-foreground text-lg italic mb-3">
                          "Rick's Black Car Service made our wedding day transportation absolutely perfect. The driver was early, the car was beautiful, and they even helped coordinate timing with our photographer. Highly recommend for any San Antonio wedding!"
                        </p>
                        <p className="text-foreground font-semibold">- Sarah & Michael, Married at Hotel Emma</p>
                      </div>
                      <div className="border-l-4 border-primary pl-6">
                        <p className="text-muted-foreground text-lg italic mb-3">
                          "We had guests coming from out of town for our Hill Country wedding. Rick's provided seamless shuttle service from the hotel to our venue in Boerne. Everyone arrived on time and safely. Worth every penny!"
                        </p>
                        <p className="text-foreground font-semibold">- Jennifer & David, Married at Tapatio Springs</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Booking Information */}
                <Card className="card-luxury my-12">
                  <CardContent className="p-10">
                    <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-8">
                      Book Your Wedding Transportation Today
                    </h2>
                    <p className="text-muted-foreground text-lg leading-relaxed mb-8">
                      Let Rick's Black Car Service be part of your perfect wedding day. Contact us today to discuss your wedding transportation needs, get a personalized quote, and reserve your date.
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                      <div className="text-center">
                        <Phone className="w-8 h-8 text-primary mx-auto mb-3" />
                        <h3 className="text-xl font-semibold text-foreground mb-2">Call Us</h3>
                        <p className="text-muted-foreground mb-2">
                          <a href="tel:+12102967409" className="text-primary hover:underline font-semibold text-lg">
                            210-296-7409
                          </a>
                        </p>
                        <p className="text-muted-foreground text-sm">Speak with our wedding specialist</p>
                      </div>

                      <div className="text-center">
                        <Heart className="w-8 h-8 text-primary mx-auto mb-3" />
                        <h3 className="text-xl font-semibold text-foreground mb-2">Email Us</h3>
                        <p className="text-muted-foreground mb-2">
                          <a href="mailto:ricksblackcarservice@outlook.com" className="text-primary hover:underline text-sm">
                            ricksblackcarservice@outlook.com
                          </a>
                        </p>
                        <p className="text-muted-foreground text-sm">Send wedding details for quote</p>
                      </div>

                      <div className="text-center">
                        <Users className="w-8 h-8 text-primary mx-auto mb-3" />
                        <h3 className="text-xl font-semibold text-foreground mb-2">Office Location</h3>
                        <p className="text-muted-foreground text-sm">
                          16034 Rough Oak Street<br />San Antonio, TX 78232
                        </p>
                      </div>
                    </div>

                    <h3 className="text-2xl font-serif font-bold text-foreground mb-4">Information to Have Ready:</h3>
                    <ul className="space-y-2 text-muted-foreground">
                      <li className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                        <span>Wedding date and day of week</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                        <span>Ceremony and reception venue names and addresses</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                        <span>Number of people needing transportation</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                        <span>Pickup locations (hotel, home, etc.)</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                        <span>Estimated timeline for the day</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                        <span>Any special requests or requirements</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                {/* Related Services */}
                <div className="prose prose-lg max-w-none my-12">
                  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-6">
                    Other Luxury Transportation Services
                  </h2>
                  <p className="text-muted-foreground text-lg mb-4">Rick's Black Car Service also provides:</p>
                  <ul className="space-y-2 text-muted-foreground text-lg">
                    <li className="flex items-start gap-3">
                      <ArrowRight className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                      <span><Link to="/airport-transportation" className="text-primary hover:underline">Airport Transportation</Link> - For wedding guests arriving at SAT</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <ArrowRight className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                      <span><Link to="/wine-tours" className="text-primary hover:underline">Texas Hill Country Wine Tours</Link> - Perfect for bachelor/bachelorette parties</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <ArrowRight className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                      <span><Link to="/special-events" className="text-primary hover:underline">Special Events Transportation</Link> - Engagement parties, rehearsal dinners</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <ArrowRight className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                      <span><Link to="/corporate-travel" className="text-primary hover:underline">Corporate Transportation</Link> - For business-related wedding travel</span>
                    </li>
                  </ul>
                </div>

                {/* Service Areas */}
                <Card className="card-luxury my-12">
                  <CardContent className="p-10">
                    <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-6">
                      Wedding Transportation Service Areas
                    </h2>
                    <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                      We provide wedding transportation throughout San Antonio and the entire Texas Hill Country region including:
                    </p>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 text-muted-foreground">
                      <li className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                        <span>San Antonio (Stone Oak, Alamo Heights, The Dominion, Downtown)</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                        <span>Boerne and Fair Oaks Ranch</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                        <span>New Braunfels and Gruene</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                        <span>Schertz and Cibolo</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                        <span>Fredericksburg wine country</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                        <span>Dripping Springs and Wimberley</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                        <span>Canyon Lake and surrounding areas</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                        <span>Austin (for intercity wedding events)</span>
                      </li>
                    </ul>
                    <p className="text-muted-foreground text-lg mt-6">
                      <Link to="/service-areas" className="text-primary hover:underline font-semibold">
                        View our complete service area coverage →
                      </Link>
                    </p>
                  </CardContent>
                </Card>

                {/* FAQ Section */}
                <ServiceFAQ faqs={faqs} />

                {/* Final CTA */}
                <div className="text-center my-12">
                  <h2 className="text-3xl font-serif font-bold text-foreground mb-6">
                    Make Your Wedding Day Transportation Perfect
                  </h2>
                  <p className="text-muted-foreground text-lg mb-8">
                    Reserve your elegant wedding transportation today. Call (210) 296-7409 to discuss your wedding day needs and receive a personalized quote.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <Button className="btn-luxury text-xl px-10 py-7" asChild>
                      <a href="tel:+12102967409">
                        <Phone className="w-6 h-6 mr-2" />
                        Call: 210-296-7409
                      </a>
                    </Button>
                    <Button className="btn-luxury text-xl px-10 py-7" asChild>
                      <a href="https://ricksblackcarservicesantonio.square.site/" target="_blank" rel="noopener noreferrer">
                        Request Wedding Quote <ArrowRight className="w-6 h-6 ml-2" />
                      </a>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Instant Quote CTA */}
          <InstantQuoteCTA />
        </main>
        <Footer />
      </div>
    </>;
};
export default WeddingTransportation;